import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { MetricAnomalies, Service, Slack } from 'webapp-types';
import { DeliveryType, GranulationTypeId } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { GroupSelected } from 'webapp/components/form/checkbox-group';

export default abstract class MetricAnomaliesBaseComponent<T> extends Component<T> {
	@service('model-manager/metric-anomalies')
	declare metricAnomaliesService: Service.ModelManager.MetricAnomalies;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;

	@tracked selectedGranulations: GranulationTypeId[] = [];
	@tracked anomalyScore = 0;
	@tracked selectedDeliveryTypes: number[] = [];
	@tracked selectedSlackChannels: Slack.Channels = [];

	deliveryTypes = [DeliveryType.EMAIL, DeliveryType.SLACK];

	anomalyScoreTooltipText = `
			<div class="tooltip-anomaly-score">
				<h6>Set the anomaly score threshold to trigger smart alerts</h6>
				<p>Anomaly score reflects the severity of the detected anomaly. Raising the threshold reduces the number of smart alerts triggered.</p>
			</div>
		`;
	checkForAnomaliesTooltipText = `
			<div class="tooltip-anomaly-score">
				<h6>Choose how often to check for anomalies</h6>
				<p>The system will check for anomalies with selected frequency and granulation. Daily checks yesterday's data, weekly checks last week's data at the start of each week, and monthly checks last month's data on the 1st.</p>
			</div>
		`;

	get weeklyGranulationTooltip() {
		return this.isWeekGranulationDisabled
			? 'For the anomaly detection feature to work with weekly granularity, set Monday as the first day of the week for the account.'
			: undefined;
	}

	get defaultGranulations() {
		const tooltipIcon: IconName = 'AlertFill';
		return [
			{ label: GranulationTypeId.DAILY, value: GranulationTypeId.DAILY },
			{
				label: GranulationTypeId.WEEKLY,
				value: GranulationTypeId.WEEKLY,
				tooltip: this.weeklyGranulationTooltip,
				tooltipIcon,
				disabled: this.isWeekGranulationDisabled,
				tooltipMaxWidth: 320,
			},
			{ label: GranulationTypeId.MONTHLY, value: GranulationTypeId.MONTHLY },
		];
	}

	get isWeekGranulationDisabled() {
		return this.accountService.firstDayOfWeek !== 1;
	}

	get isEmailSelected(): boolean {
		return this.selectedDeliveryTypes.includes(DeliveryType.EMAIL);
	}

	get isSlackSelected(): boolean {
		return this.selectedDeliveryTypes.includes(DeliveryType.SLACK);
	}

	get isDailyGranulationSelected(): boolean {
		return this.selectedGranulations.includes(GranulationTypeId.DAILY);
	}

	get isWeeklyGranulationSelected(): boolean {
		return this.selectedGranulations.includes(GranulationTypeId.WEEKLY);
	}

	get isMonthlyGranulationSelected(): boolean {
		return this.selectedGranulations.includes(GranulationTypeId.MONTHLY);
	}

	get slackChannels() {
		return this.metricAnomaliesService.topItemDefaultSettings?.notifications.slack ?? [];
	}

	get isFormInvalid() {
		return this.selectedGranulations.length === 0 || this.selectedDeliveryTypes.length === 0;
	}

	@action
	handleGranulationsChange(value: GroupSelected) {
		this.selectedGranulations = value as GranulationTypeId[];
	}

	@action
	handleAnomalyScoreChange(value: number) {
		this.anomalyScore = value;
	}

	@action
	updateDelivery(value: number[]): void {
		this.selectedDeliveryTypes = value;

		if (!this.isSlackSelected) {
			this.updateSlackChannels([]);
		}
	}

	@action
	updateSlackChannels(value: Slack.Channels): void {
		this.analyticsService.event(TrackingEvent.SLACK_CHANNELS_SELECTED, {
			origin: 'smart-alert',
		});
		this.selectedSlackChannels = value;
	}

	protected serializeSettings() {
		return {
			anomalyScore: this.anomalyScore,
			granulations: {
				daily: this.isDailyGranulationSelected,
				weekly: this.isWeeklyGranulationSelected,
				monthly: this.isMonthlyGranulationSelected,
			},
			notifications: { email: this.isEmailSelected, slack: this.selectedSlackChannels },
		};
	}

	protected parseAndSetDefaultState(settings: MetricAnomalies.Settings) {
		if (!settings) {
			return; // Exit if no settings are available
		}

		// Set granulations
		if (settings.granulations.daily) {
			this.selectedGranulations.push(GranulationTypeId.DAILY);
		}
		if (settings.granulations.weekly) {
			this.selectedGranulations.push(GranulationTypeId.WEEKLY);
		}
		if (settings.granulations.monthly) {
			this.selectedGranulations.push(GranulationTypeId.MONTHLY);
		}

		// Set anomaly score
		this.anomalyScore = settings.anomalyScore ?? 0;

		// Set delivery types and channels
		if (settings.notifications.email) {
			this.selectedDeliveryTypes.push(DeliveryType.EMAIL);
		}

		const slackChannels = settings.notifications.slack;
		if (Array.isArray(slackChannels) ? slackChannels.length > 0 : slackChannels) {
			this.selectedDeliveryTypes.push(DeliveryType.SLACK);
			this.selectedSlackChannels = slackChannels;
		}
	}
}
